/**
 * Umsatzgrenzen der Kleinunternehmerregelung bzw. der MWST-Pflicht je Land, EINE Regel für Agentur- und Creator-Portal
 * (SAAS-R3-5). Stand 2026, ohne Gewähr, keine Steuerberatung.
 *
 * - DE § 19 UStG (seit 2025): Vorjahr bis 25.000 €, laufendes Jahr bis 100.000 € (Gesamtumsatz netto); über 100.000 € gilt die Regelbesteuerung ab
 *   dem Umsatz, der die Grenze überschreitet.
 * - AT § 6 Abs. 1 Z 27 UStG (seit 2025): 55.000 € brutto im Vorjahr und im laufenden Jahr. Eine einmalige Überschreitung im
 *   laufenden Jahr um höchstens 10 % (bis 60.500 €) ist unschädlich: die Befreiung gilt dieses Jahr weiter, im Folgejahr nicht mehr.
 *   Über 60.500 € gilt die Regelbesteuerung ab dem Umsatz, der die Toleranzgrenze überschreitet.
 * - CH Art. 10 Abs. 2 MWSTG, LI MWSTG (FL): MWST-Pflicht ab 100.000 CHF Umsatz im Jahr (befreit: weniger als 100.000 CHF).
 * Welcher Umsatz zählt (netto/brutto, Leistungsort Inland), regelt own-revenue.ts (smallBusinessTurnover).
 */
import type { Cents } from "./types.js";
import { normalizeCountry } from "./countries.js";

export interface SmallBusinessRule {
  country: "DE" | "AT" | "CH" | "LI";
  currency: "EUR" | "CHF";
  /** Grenze im laufenden Jahr */
  currentCents: Cents;
  /** Grenze für das Vorjahr (entscheidet über das Folgejahr), null ohne eigene Vorjahresgrenze */
  previousCents: Cents | null;
  /** AT: einmalige Toleranz im laufenden Jahr (Befreiung bleibt, endet mit dem Jahr) */
  toleranceCents: Cents | null;
  /** Rechtsgrundlage im Wortlaut des Landes */
  law: string;
}

export function smallBusinessRule(country: string | null | undefined): SmallBusinessRule | null {
  const c = normalizeCountry(country ?? null);
  if (c === "DE") return { country: "DE", currency: "EUR", currentCents: 10_000_000, previousCents: 2_500_000, toleranceCents: null, law: "§ 19 UStG" };
  if (c === "AT") return { country: "AT", currency: "EUR", currentCents: 5_500_000, previousCents: 5_500_000, toleranceCents: 6_050_000, law: "§ 6 Abs. 1 Z 27 UStG" };
  if (c === "CH") return { country: "CH", currency: "CHF", currentCents: 10_000_000, previousCents: null, toleranceCents: null, law: "Art. 10 Abs. 2 MWSTG" };
  if (c === "LI") return { country: "LI", currency: "CHF", currentCents: 10_000_000, previousCents: null, toleranceCents: null, law: "Art. 10 Abs. 2 MWSTG (FL)" };
  return null;
}

/**
 * ok: unter 80 % der Grenze · near: ab 80 % · tolerance (nur AT, laufendes Jahr): über der Grenze, aber in der Toleranz; die
 * Befreiung gilt dieses Jahr weiter und endet zum Jahreswechsel · over: Befreiung endet (laufendes Jahr: ab dem Umsatz, der die
 * Grenze bzw. Toleranz überschreitet; Vorjahr: für das Folgejahr).
 */
export type SmallBusinessLevel = "ok" | "near" | "tolerance" | "over";

export interface SmallBusinessCheck {
  level: SmallBusinessLevel;
  /** maßgebliche Grenze (laufendes Jahr: Grenze ohne Toleranz; Vorjahr: Vorjahresgrenze) */
  capCents: Cents;
  toleranceCents: Cents | null;
  /** Anteil an capCents */
  ratio: number;
}

export function checkSmallBusinessLimit(rule: SmallBusinessRule, yearCents: Cents, period: "current" | "previous" = "current"): SmallBusinessCheck {
  const cap = period === "previous" ? rule.previousCents ?? rule.currentCents : rule.currentCents;
  const tolerance = period === "current" ? rule.toleranceCents : null;
  const ratio = cap > 0 ? yearCents / cap : 0;
  // DE/AT: „bis“ zur Grenze ist befreit; erst darüber endet die Befreiung bzw. greift die Toleranz.
  // CH/LI: befreit ist ein Umsatz von „weniger als“ 100.000 CHF; genau 100.000 CHF begründen die MWST-Pflicht.
  const swiss = rule.country === "CH" || rule.country === "LI";
  const level: SmallBusinessLevel = (swiss ? yearCents >= cap : yearCents > (tolerance ?? cap)) ? "over"
    : yearCents > cap ? "tolerance"
    : ratio >= 0.8 ? "near" : "ok";
  return { level, capCents: cap, toleranceCents: tolerance, ratio };
}
